import { AppSource } from '@application/config';
import { Capacitor } from '@capacitor/core';

/** Anything that can drive a root navigation — `Router` and `RouterStore` both fit. */
export type RootNavigator = { navigate: (path: string[]) => unknown };

/** Only `appSource` is read; keeps the helpers trivial to unit test. */
export type ShellAppConfig = { appSource?: AppSource | number | null };

/**
 * Apps that ship the login shell and the authenticated shell as two root `path: ''`
 * siblings inside a single `ion-router-outlet`: every native bundle, plus the Ediz
 * guardian app (self-contained on the web too — login and home live in one app).
 */
export function isSelfContainedShellApp(appConfig: ShellAppConfig): boolean {
  return (
    Capacitor.isNativePlatform() || appConfig?.appSource === AppSource.EdizApp
  );
}

/**
 * Leave the login shell for the authenticated root.
 *
 * On a self-contained app an in-app hop to `/` leaves the login page on Ionic's view
 * stack, so the authenticated shell (and Home) mount INSIDE `lib-accounts-ediz-login-base`
 * instead of replacing it. A full reload rebuilds the stack straight from the
 * authenticated shell. The session is already persisted in `localStorage`, so
 * `AuthStore.onInit` rehydrates it on boot and the login guard lets `/` through.
 *
 * Only reloads when there IS a session: logged out, `/` resolves back to the login
 * screen inside the same shell, and reloading there would just cost a boot (and drop
 * any success alert on the way). With no session the target is `login` itself rather
 * than `/` — that is the only place `/` can land (the accounts apps redirect it, the
 * guardian app's root guard bounces it), and going straight there skips a redirect
 * hop that left the native app showing nothing.
 */
export function enterAuthenticatedShell(
  navigator: RootNavigator,
  appConfig: ShellAppConfig,
  isLoggedIn: boolean
): void {
  if (isLoggedIn && isSelfContainedShellApp(appConfig)) {
    window.location.href = '/';
    return;
  }

  navigator.navigate([isLoggedIn ? '/' : 'login']);
}
